import classNames from 'classnames/bind';
import { isSafari } from 'react-device-detect';
import React, { MouseEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Divider, Loader } from '@creditsights/styleguide-web';
import { useNavigate } from 'react-router-dom';
import NoResults from '../../../components/NoResults';
import PdfViewer from '../../../components/PdfViewer';
import { useOnLoadImages } from '../../../hooks/common';
import { ArticleContent as ArticleContentModel, Component } from '../../../models';
import { getArticleMouseClickHandler } from '../../../utils/articleUtils';
import { isBulletComponent } from '../../../utils/tagUtils';
import { PDF_PRINT_IFRAME_ID } from './ArticleContent.constants';
import { getSummaryTitleId, isPdfViewerSupported, MINIMUM_PDF_VIEWER_HEIGHT } from './ArticleContent.logic';
import styles from './ArticleContent.module.scss';
import { getArticleDataTestId, PreviewArticle } from '../../../components/ArticlePreview/ArticlePreview.logic';

const cx = classNames.bind(styles);

interface ArticleContentProps {
  articleBodyClassName?: string;
  bodyClassName?: string;
  content: ArticleContentModel;
  summaryClassName?: string;
  isCompact?: boolean;
  isArticlePreview?: boolean;
  setImagesLoaded?: (boolean) => void;
  onArticleClick?: (article: PreviewArticle, event: MouseEvent<HTMLElement>) => void;
  isSectorReview?: boolean;
  print?: boolean;
}

const renderSummaryTitle = ({ file, tags, title }: Component) => {
  if (file && ((file.pdfContent && !isPdfViewerSupported()) || !file.pdfContent)) {
    return (
      <a href={file.url} rel="noopener noreferrer" target="_blank" download>
        {title}
      </a>
    );
  }

  if (file?.pdfContent) {
    return null;
  }

  return isBulletComponent(tags) ? 'Executive Summary' : title;
};

const getPdfUrl = (fileUrl: string, timestamp: number) => {
  const url = `${fileUrl}#view=FitH&zoom=FitH&toolbar=0`;
  // timestamp is required to trigger object tag rerender for Safari browser
  return isSafari ? `${url}&timestamp=${timestamp}` : url;
};

const renderPdfViewer = ({ file }: Component, showPdfViewer: boolean, timestamp: number, print = false) => {
  if (!file?.pdfContent || !isPdfViewerSupported()) {
    return null;
  }
  if (!showPdfViewer) {
    return (
      <div style={{ height: MINIMUM_PDF_VIEWER_HEIGHT }}>
        <Loader position="center" />
      </div>
    );
  }

  if (print) {
    return (
      <iframe
        title="print-pdf"
        id={PDF_PRINT_IFRAME_ID}
        data-testid={PDF_PRINT_IFRAME_ID}
        src={getPdfUrl(file.url, timestamp)}
        width="100%"
        height="100%"
      />
    );
  }

  return (
    <div data-testid="content-summary-pdf-content">
      <PdfViewer file={file.url} />
    </div>
  );
};

const renderComponents = (
  content: ArticleContentModel,
  showPdfViewer: boolean,
  isArticlePreview: boolean,
  isCompact: boolean,
  timestamp: number,
  isSectorReview: boolean,
  articleBodyClassName?: string,
  summaryClassName?: string,
  print?: boolean
) => {
  const { components, summary } = content;
  if (!components.length) {
    if (summary) {
      return (
        <div
          className={cx('summary', summaryClassName)}
          data-testid={getArticleDataTestId('basic-summary', isArticlePreview)}
          dangerouslySetInnerHTML={{ __html: summary }}
        />
      );
    }

    return <NoResults title="No report found" />;
  }

  const bodyIndex = components.findIndex((component) => !component.title && !isBulletComponent(component.tags));
  const summaryComponents = bodyIndex >= 0 ? components.slice(0, bodyIndex) : components;
  const bodyComponents = bodyIndex >= 0 ? components.slice(bodyIndex).filter((component) => !!component.body) : [];

  return (
    <div
      className={cx('article-body', articleBodyClassName)}
      data-testid={getArticleDataTestId('component-body', isArticlePreview)}
    >
      {summaryComponents.map((component, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`summary-component-${index}`}>
          <h3 data-testid="content-summary-title" id={getSummaryTitleId(index)}>
            {renderSummaryTitle(component)}
          </h3>
          {component.body && (
            <p data-testid="content-summary-content" dangerouslySetInnerHTML={{ __html: component.body }} />
          )}
          {renderPdfViewer(component, showPdfViewer, timestamp, print)}
        </React.Fragment>
      ))}
      {!!summaryComponents.length && !!bodyComponents.length && (
        <div className={cx('divider-container')}>
          <Divider />
        </div>
      )}
      {bodyComponents.map((component, index) => (
        <div
          className={cx({
            'sector-review': isSectorReview,
            'two-column-view': !isArticlePreview && isSectorReview,
          })}
          // eslint-disable-next-line react/no-array-index-key
          key={`body-component-${index}`}
          data-testid="content-body-component"
          dangerouslySetInnerHTML={{ __html: component.body! }}
        />
      ))}
    </div>
  );
};

const ArticleContent = ({
  articleBodyClassName,
  bodyClassName,
  content,
  summaryClassName,
  isCompact,
  isArticlePreview,
  setImagesLoaded,
  onArticleClick,
  isSectorReview,
  print,
}: ArticleContentProps): React.ReactElement => {
  const contentRef = useRef<HTMLDivElement>(null);
  const imagesLoaded = useOnLoadImages(contentRef, [content]);
  const navigate = useNavigate();
  const mouseClickHandler = getArticleMouseClickHandler(onArticleClick, navigate);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [timestamp] = useState<number>(Date.now());

  useLayoutEffect(() => {
    setShowPdfViewer(true);
  }, []);

  useEffect(() => {
    contentRef.current?.querySelectorAll('table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.classList.add('table-container');
      table.parentNode?.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }, [content]);

  useEffect(() => {
    setImagesLoaded?.(imagesLoaded);
  }, [imagesLoaded]);

  return (
    <div
      className={cx('body', { 'article-tab-content': isCompact, 'highlighted-links': !isSectorReview }, bodyClassName)}
      data-testid={getArticleDataTestId('article-content-body', !!isArticlePreview)}
      ref={contentRef}
      onClick={mouseClickHandler}
    >
      {renderComponents(
        content,
        showPdfViewer,
        !!isArticlePreview,
        !!isCompact,
        timestamp,
        !!isSectorReview,
        articleBodyClassName,
        summaryClassName,
        print
      )}
    </div>
  );
};

export default ArticleContent;
